import React, { useCallback, useEffect, useMemo, useState } from "react";
import debounce from "lodash.debounce";
import api from "../../../Services/api";
import Swal from "sweetalert2";
import { FiArrowDown, FiArrowUp, FiSearch, FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { MdPlaylistAdd, MdPlaylistRemove } from "react-icons/md";
import { FaGear } from "react-icons/fa6";
import * as Enum from "../../../Enum/Enum";
import "./gerenciarGrupoQuestoesModal.css";

const GerenciarGrupoQuestoesModal = ({ open, grupo, onClose, onSaved }) => {
  const MIN_ITENS_POR_PAGINA = 1;
  const ALTURA_APROX_ITEM = 54;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [itensGrupo, setItensGrupo] = useState([]);
  const [questoesDisponiveis, setQuestoesDisponiveis] = useState([]);
  const [filtroDisponiveis, setFiltroDisponiveis] = useState("");
  const [debouncedFiltroDisponiveis, setDebouncedFiltroDisponiveis] = useState("");
  const [pageDisponiveis, setPageDisponiveis] = useState(1);
  const [pageGrupo, setPageGrupo] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(MIN_ITENS_POR_PAGINA);

  const handleDebounce = useCallback(
    debounce((value) => {
      setDebouncedFiltroDisponiveis(value);
    }, 350),
    []
  );

  useEffect(() => {
    return () => handleDebounce.cancel();
  }, [handleDebounce]);

  const carregarDados = useCallback(async () => {
    if (!grupo?.IDGrupoQuestao || !open) return;
    try {
      setLoading(true);
      const [itensRes, questoesRes] = await Promise.all([
        api.get("/chkgrupoquestaoitens", {
          params: { IDGrupoQuestao: grupo.IDGrupoQuestao },
        }),
        api.get("/chkquestoesstats", {
          params: { page: 1, limit: 9999 },
        }),
      ]);

      const itensOrdenados = (itensRes.data?.data || []).sort(
        (a, b) => Number(a.ordem || 0) - Number(b.ordem || 0)
      );
      setItensGrupo(itensOrdenados);
      setQuestoesDisponiveis(questoesRes.data?.data || []);
    } catch (error) {
      console.error("Erro ao carregar gerenciamento do grupo", error);
      Swal.fire("Erro", "Não foi possível carregar os dados do grupo.", "error");
    } finally {
      setLoading(false);
    }
  }, [grupo?.IDGrupoQuestao, open, debouncedFiltroDisponiveis]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const idsNoGrupo = useMemo(
    () => new Set(itensGrupo.map((item) => Number(item.IDQuestao))),
    [itensGrupo]
  );
  const disponiveisFiltradas = useMemo(
    () =>
      questoesDisponiveis.filter(
        (questao) => {
          if (Number(questao.situacao) !== 1) return false;
          if (idsNoGrupo.has(Number(questao.IDQuestao))) return false;

          const termo = debouncedFiltroDisponiveis.trim().toLowerCase();
          if (!termo) return true;

          const tipoTexto = String(Enum.ETipoQuestao?.[questao.tipo] || "").toLowerCase();
          return (
            String(questao.titulo || "").toLowerCase().includes(termo) ||
            String(questao.descricao || "").toLowerCase().includes(termo) ||
            String(questao.IDQuestao || "").includes(termo) ||
            tipoTexto.includes(termo)
          );
        }
      ),
    [questoesDisponiveis, idsNoGrupo, debouncedFiltroDisponiveis]
  );

  const questaoPorId = useMemo(() => {
    const mapa = new Map();
    questoesDisponiveis.forEach((questao) => {
      mapa.set(Number(questao.IDQuestao), questao);
    });
    return mapa;
  }, [questoesDisponiveis]);

  const totalPagesDisponiveis = Math.max(
    1,
    Math.ceil(disponiveisFiltradas.length / itensPorPagina)
  );
  const totalPagesGrupo = Math.max(1, Math.ceil(itensGrupo.length / itensPorPagina));

  const disponiveisPaginadas = useMemo(() => {
    const inicio = (pageDisponiveis - 1) * itensPorPagina;
    return disponiveisFiltradas.slice(inicio, inicio + itensPorPagina);
  }, [disponiveisFiltradas, pageDisponiveis, itensPorPagina]);

  const itensGrupoPaginados = useMemo(() => {
    const inicio = (pageGrupo - 1) * itensPorPagina;
    return itensGrupo.slice(inicio, inicio + itensPorPagina);
  }, [itensGrupo, pageGrupo, itensPorPagina]);

  useEffect(() => {
    if (pageDisponiveis > totalPagesDisponiveis) setPageDisponiveis(1);
  }, [pageDisponiveis, totalPagesDisponiveis]);

  useEffect(() => {
    if (pageGrupo > totalPagesGrupo) setPageGrupo(1);
  }, [pageGrupo, totalPagesGrupo]);

  useEffect(() => {
    if (!open) return undefined;

    const recalcularItensPorPagina = () => {
      const listas = Array.from(document.querySelectorAll(".gerenciarGrupo-modal .gerenciar-list"));
      if (!listas.length) return;

      const menorAlturaLista = Math.min(
        ...listas.map((lista) => (lista instanceof HTMLElement ? lista.clientHeight : 0)).filter(Boolean)
      );
      if (!menorAlturaLista) return;

      const calculado = Math.floor(menorAlturaLista / ALTURA_APROX_ITEM);
      setItensPorPagina(Math.max(MIN_ITENS_POR_PAGINA, calculado));
    };

    recalcularItensPorPagina();
    const observer = new ResizeObserver(() => recalcularItensPorPagina());
    const modal = document.querySelector(".gerenciarGrupo-modal");
    if (modal instanceof HTMLElement) observer.observe(modal);
    window.addEventListener("resize", recalcularItensPorPagina);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", recalcularItensPorPagina);
    };
  }, [open]);

  if (!open || !grupo) return null;

  const adicionarQuestao = async (questao) => {
    try {
      const ordem = itensGrupo.length + 1;
      await api.post("/chkgrupoquestaoitem", {
        IDGrupoQuestao: grupo.IDGrupoQuestao,
        IDQuestao: questao.IDQuestao,
        ordem,
      });
      await carregarDados();
      setPageDisponiveis(1);
      setPageGrupo(1);
    } catch (error) {
      console.error("Erro ao adicionar questão no grupo", error);
      Swal.fire(
        "Erro",
        error?.response?.data?.errors?.default || "Não foi possível adicionar a questão.",
        "error"
      );
    }
  };

  const removerQuestao = async (item) => {
    const confirm = await Swal.fire({
      title: "Remover questão do grupo?",
      text: "A questão será desvinculada deste grupo.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remover",
      cancelButtonText: "Cancelar",
      cancelButtonColor: "var(--color-btnCancelar-danger)",
      customClass: { cancelButton: "swal-cancel-danger" },
    });
    if (!confirm.isConfirmed) return;

    try {
      await api.delete(`/chkgrupoquestaoitem/${item.IDGrupoQuestao_Item}`);
      await carregarDados();
      setPageDisponiveis(1);
      setPageGrupo(1);
    } catch (error) {
      console.error("Erro ao remover questão do grupo", error);
      Swal.fire(
        "Erro",
        error?.response?.data?.errors?.default || "Não foi possível remover a questão.",
        "error"
      );
    }
  };

  const mover = (index, direction) => {
    const destino = index + direction;
    if (destino < 0 || destino >= itensGrupo.length) return;
    const copia = [...itensGrupo];
    const [item] = copia.splice(index, 1);
    copia.splice(destino, 0, item);
    setItensGrupo(copia.map((it, idx) => ({ ...it, ordem: idx + 1 })));
  };

  const salvarOrdenacao = async () => {
    try {
      setSaving(true);
      Swal.fire({
        title: "Salvando...",
        text: "Atualizando a ordem das questões no grupo.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });
      // Fase 1: aplica ordens temporárias para evitar conflito de unicidade (ordem por grupo).
      const ordemTemporariaBase = 1000000;
      await Promise.all(
        itensGrupo.map((item, index) =>
          api.put(`/chkgrupoquestaoitem/${item.IDGrupoQuestao_Item}`, {
            IDGrupoQuestao: Number(item.IDGrupoQuestao),
            IDQuestao: Number(item.IDQuestao),
            ordem: ordemTemporariaBase + index,
          })
        )
      );

      // Fase 2: aplica ordens finais.
      await Promise.all(
        itensGrupo.map((item, index) =>
          api.put(`/chkgrupoquestaoitem/${item.IDGrupoQuestao_Item}`, {
            IDGrupoQuestao: Number(item.IDGrupoQuestao),
            IDQuestao: Number(item.IDQuestao),
            ordem: index + 1,
          })
        )
      );
      Swal.close();
      await Swal.fire("Sucesso", "Ordem atualizada com sucesso.", "success");
      onSaved?.();
      await carregarDados();
    } catch (error) {
      console.error("Erro ao salvar ordenação do grupo", error);
      Swal.fire(
        "Erro",
        error?.response?.data?.errors?.default || "Não foi possível salvar a ordem.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="gerenciarGrupo-overlay">
      <div className="gerenciarGrupo-modal">
        <div className="modal-header">
          <h3 className="gerenciar-title">
            <FaGear />
            Gerenciar questões do grupo
          </h3>
          <button className="close-btn" onClick={onClose} disabled={saving}>
            <FiX />
          </button>
        </div>

        <div className="gerenciarGrupo-info">
          <strong>{grupo.descricao}</strong>
        </div>

        {loading ? (
          <p className="nenhum">Carregando...</p>
        ) : (
          <div className="gerenciarGrupo-content">
            <div className="gerenciar-col">
              <h6>Questões disponíveis</h6>
              <div className="modal-search">
                <FiSearch className="icon" />
                <input
                  type="text"
                  placeholder="Buscar questão..."
                  value={filtroDisponiveis}
                  onChange={(e) => {
                    setFiltroDisponiveis(e.target.value);
                    setPageDisponiveis(1);
                    handleDebounce(e.target.value);
                  }}
                />
              </div>
              <div className="gerenciar-list">
                {!disponiveisFiltradas.length && <p className="nenhum">Nenhuma questão disponível.</p>}
                {disponiveisPaginadas.map((questao) => (
                  <div key={questao.IDQuestao} className="gerenciar-item">
                    <div className="gerenciar-item-main">
                      <div className="item-header">
                        <span className="item-id">#{questao.IDQuestao}</span>
                        <span className="item-titulo">{questao.titulo}</span>
                        <span className="item-tipo">{Enum.ETipoQuestao?.[questao.tipo] || "-"}</span>
                      </div>
                    </div>
                    <div className="gerenciar-item-actions gerenciar-item-actions--disponiveis">
                      <button
                        className="btn-adicionar"
                        onClick={() => adicionarQuestao(questao)}
                        disabled={saving}
                        title="Adicionar questão ao grupo"
                      >
                        <MdPlaylistAdd />
                        Add
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mini-pagination">
                <button
                  type="button"
                  onClick={() => setPageDisponiveis((p) => Math.max(p - 1, 1))}
                  disabled={pageDisponiveis === 1}
                >
                  <FiChevronLeft />
                </button>
                <span>
                  Página {pageDisponiveis} de {totalPagesDisponiveis}
                </span>
                <button
                  type="button"
                  onClick={() => setPageDisponiveis((p) => Math.min(p + 1, totalPagesDisponiveis))}
                  disabled={pageDisponiveis >= totalPagesDisponiveis}
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>

            <div className="gerenciar-col">
              <h6>Questões do grupo</h6>
              <div className="gerenciar-list">
                {!itensGrupo.length && <p className="nenhum">Nenhuma questão no grupo.</p>}
                {itensGrupoPaginados.map((item, localIndex) => {
                  const indexGlobal = (pageGrupo - 1) * itensPorPagina + localIndex;
                  const questaoRef = questaoPorId.get(Number(item.IDQuestao));
                  return (
                    <div key={item.IDGrupoQuestao_Item} className="gerenciar-item">
                      <div className="ordem-rail">
                        <button
                          className="ordem-rail-btn"
                          onClick={() => mover(indexGlobal, -1)}
                          disabled={indexGlobal === 0 || saving}
                          title="Mover para cima"
                        >
                          <FiArrowUp />
                        </button>
                        <button
                          className="ordem-rail-btn"
                          onClick={() => mover(indexGlobal, 1)}
                          disabled={indexGlobal === itensGrupo.length - 1 || saving}
                          title="Mover para baixo"
                        >
                          <FiArrowDown />
                        </button>
                      </div>
                      <div className="gerenciar-item-main">
                        <div className="item-header">
                          <span className="ordem-chip">{indexGlobal + 1}</span>
                          <span className="item-id">#{item.IDQuestao}</span>
                          <span className="item-titulo">
                            {item.questaoTitulo || questaoRef?.titulo || `Questão ${item.IDQuestao}`}
                          </span>
                          <span className="item-tipo">
                            {Enum.ETipoQuestao?.[questaoRef?.tipo] || "-"}
                          </span>
                        </div>
                      </div>
                      <div className="gerenciar-item-actions gerenciar-item-actions--grupo">
                        <button className="btn-remover" onClick={() => removerQuestao(item)} disabled={saving}>
                          <MdPlaylistRemove />
                          Remover
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mini-pagination">
                <button
                  type="button"
                  onClick={() => setPageGrupo((p) => Math.max(p - 1, 1))}
                  disabled={pageGrupo === 1}
                >
                  <FiChevronLeft />
                </button>
                <span>
                  Página {pageGrupo} de {totalPagesGrupo}
                </span>
                <button
                  type="button"
                  onClick={() => setPageGrupo((p) => Math.min(p + 1, totalPagesGrupo))}
                  disabled={pageGrupo >= totalPagesGrupo}
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={saving}>
            Fechar
          </button>
          <button className="confirm-btn" onClick={salvarOrdenacao} disabled={saving || loading || !itensGrupo.length}>
            Salvar ordem
          </button>
        </div>
      </div>
    </div>
  );
};

export default GerenciarGrupoQuestoesModal;
